import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, List, HardDrive, Database, Save, ShieldCheck, HelpCircle, Zap, Layout, Settings, Info, Cloud, Share2, Smartphone, HardDriveDownload } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Fazer Backup das Configurações IPTV", href: "/blog/como-fazer-backup-configuracoes-iptv" }
  ];

  const faq = [
    {
      question: "O backup salva os filmes no meu aparelho?",
      answer: "Não. O backup salva apenas os 'Metadados' (listas de favoritos, progresso de séries, histórico). Os filmes continuam sendo transmitidos do servidor da P2 PRO por streaming."
    },
    {
      question: "Posso restaurar um backup de Android no iOS?",
      answer: "Depende do aplicativo. Players que usam sincronização via nuvem (como o Cloud Stream) permitem. Em players manuais, os formatos de arquivo podem ser incompatíveis."
    },
    {
      question: "Onde ficam guardados os arquivos de backup?",
      answer: "Se for backup local, geralmente ficam na pasta 'Download' ou na pasta interna do aplicativo. Se for na nuvem, ficam vinculados ao seu e-mail de login."
    },
    {
      question: "Com que frequência devo salvar meus dados?",
      answer: "Sempre que organizar uma nova lista de favoritos ou quando estiver planejando trocar de dispositivo ou resetar seu TV Box."
    }
  ];

  const benefits = [
    {
      title: "Zero Perda de Dados",
      description: "Mantenha sua organização de canais intacta, independente do aparelho.",
      icon: <Save className="w-6 h-6" />
    },
    {
      title: "Migração Transparente",
      description: "Troque de TV ou Celular sem precisar reconfigurar tudo do zero.",
      icon: <Share2 className="w-6 h-6" />
    },
    {
      title: "Continuidade de Mídia",
      description: "Saiba exatamente qual episódio da sua série você assistiu por último.",
      icon: <Database className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Seleção de Instância",
      description: "Identifique as configurações e favoritos que deseja preservar."
    },
    {
      title: "Exportação Lógica",
      description: "O app gera um pacote de dados comprimido com suas preferências."
    },
    {
      title: "Armazenamento Externo",
      description: "Salve o arquivo na nuvem ou em um dispositivo físico de backup."
    },
    {
      title: "Importação e Validação",
      description: "Carregue os dados no novo dispositivo e valide a integridade."
    }
  ];

  const diferenciais = [
    {
      title: "Smart Cloud Sync",
      description: "Nossos apps (One X/P2 PLAY) sincronizam seus favoritos em tempo real via nuvem P2 PRO.",
      icon: <Cloud className="w-6 h-6" />
    },
    {
      title: "Universal XML Export",
      description: "Formatos de dados compatíveis com os principais softwares de gerenciamento de mídia.",
      icon: <HardDriveDownload className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. Introdução: A Importância da Preservação Digital",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Para o usuário comum de IPTV, a lista de canais é apenas entretenimento. Para o entusiasta da <strong>P2 PRO</strong>, a configuração é um ambiente personalizado. Foram horas selecionando canais favoritos, organizando listas de filmes e ajustando parâmetros de decodificação. Perder tudo isso ao trocar de dispositivo ou em um reset acidental é um retrocesso. O backup de dados é a única garantia de continuidade da sua experiência de streaming.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Neste guia denso, mergulhamos nas técnicas de preservação de dados. Ensinamos a diferença entre backups locais e na nuvem, como exportar arquivos de configuração profissional e as estratégias para manter seu histórico de mídia sincronizado entre todas as telas da sua casa.
          </p>
        </div>
      )
    },
    {
      title: "2. Tipos de Backup: Local vs Nuvem",
      type: "featured" as const,
      content: (
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white/5 p-8 rounded-3xl border border-white/10 space-y-4">
            <h4 className="text-white font-bold flex items-center gap-2"><HardDrive className="w-6 h-6 text-electric" /> Backup Local (.db / .xml)</h4>
            <p className="text-sm text-muted-foreground">O aplicativo gera um arquivo físico. <strong>Vantagem:</strong> Independência total de internet para restaurar. <strong>Desvantagem:</strong> Você precisa mover o arquivo manualmente (via pendrive ou Google Drive) para o novo aparelho.</p>
          </div>
          <div className="bg-white/5 p-8 rounded-3xl border border-white/10 space-y-4">
            <h4 className="text-white font-bold flex items-center gap-2"><Cloud className="w-6 h-6 text-electric" /> Backup em Nuvem (Cloud Sync)</h4>
            <p className="text-sm text-muted-foreground">Disponível nos apps oficiais P2 PRO. Suas preferências são vinculadas ao seu login. <strong>Vantagem:</strong> Automático e transparente. <strong>Desvantagem:</strong> Requer conexão ativa com o servidor de metadados para sincronização inicial.</p>
          </div>
        </div>
      )
    },
    {
      title: "3. Passo a Passo: Exportando Favoritos no One X e P2 PLAY",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <div>
            <h4 className="text-white text-xl font-bold mb-4">Fase 1: Preparação do Arquivo</h4>
            <p className="text-muted-foreground">Dentro do app, acesse <em>Settings {'>'} Backup {'>'} Create Backup</em>. O sistema irá gerar um arquivo com nome similar a <code>iptv_backup_2026_06.db</code>. Este arquivo contém todo o seu banco de dados de preferências.</p>
          </div>
          <div className="bg-electric/5 p-8 rounded-3xl border border-electric/20">
            <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Info className="w-6 h-6" /> Alerta de Integridade</h4>
            <p className="text-muted-foreground">Nunca altere o nome ou a extensão do arquivo de backup manualmente. O player utiliza checksums para validar se o arquivo não foi corrompido. Se você renomear, o app pode recusar a importação.</p>
          </div>
          <div>
            <h4 className="text-white text-xl font-bold mb-4">Fase 2: Transferência de Dados</h4>
            <p className="text-muted-foreground">Utilize um gerenciador de arquivos (como o ES File Explorer no Android) para mover o arquivo de backup para um pendrive ou faça o upload para sua conta do Telegram/Google Drive para tê-lo acessível em qualquer lugar.</p>
          </div>
        </div>
      )
    },
    {
      title: "4. Restaurando em um Novo Dispositivo",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Para restaurar seus dados em uma nova Smart TV ou TV Box, siga este protocolo rigoroso:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Instale a Mesma Versão do App</h5>
                <p className="text-sm text-muted-foreground">Backups gerados em versões muito antigas podem não funcionar em versões novas. Certifique-se de que ambos os aparelhos estão com o app atualizado.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Faça o Login Antes de Restaurar</h5>
                <p className="text-sm text-muted-foreground">O backup restaura preferências, mas a validade da linha depende do login inicial. Primeiro logue com seu usuário e senha, depois importe o arquivo de favoritos.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Smartphone className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Reinicie o Player</h5>
                <p className="text-sm text-muted-foreground">Após a mensagem de 'Restore Successful', feche o aplicativo e abra novamente. Isso força o player a ler o novo banco de dados de favoritos na inicialização.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Central de Ajuda IPTV"
        title="Como Fazer Backup das Configurações IPTV: Guia Denso 2026"
        subtitle="Proteja seu histórico e favoritos: o manual completo para salvar e migrar suas preferências entre dispositivos."
        heroImage="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "Horas de organização de listas, marcações de episódios assistidos e ajustes finos de imagem não podem ser perdidos em uma troca de aparelho ou falha técnica. O backup de dados é a rede de segurança que todo usuário de elite da P2 PRO deve dominar para garantir a continuidade da sua experiência digital.",
          "Neste guia denso, exploramos as entranhas técnicas da exportação e importação de dados de IPTV. Explicamos os diferentes formatos de arquivo, como utilizar a nuvem para sincronização em tempo real e como garantir que seus dados migrem de forma transparente entre TVs, smartphones e computadores.",
          "Transforme sua configuração em um ativo digital móvel e nunca mais comece do zero, mantendo sua personalização intacta em qualquer lugar do mundo."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="💾 Quer Sincronização Automática em todos os Apps?"
        ctaDescription="Assine nossos planos Premium e esqueça os arquivos manuais. Seus favoritos acompanham seu login automaticamente."
        ctaButtonText="Ver Planos com Cloud Sync"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/como-fazer-backup-configuracoes-iptv")({
  head: () => ({
    meta: [
      { title: "Como Fazer Backup das Configurações IPTV - Guia Denso 2026" },
      { name: "description", content: "O manual mais completo sobre backup e migração de dados IPTV. Aprenda a salvar favoritos, histórico de séries e configurações personalizadas em qualquer player." },
    ],
  }),
  component: ArticlePage,
});
